import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import SimpleTable from "components/global/SimpleTable";
import { useHistory, useLocation } from "react-router-dom";
import { fetchAndSetApplications, handleDownloadLoansPerformanceProgramData } from "./EdoPerformanceLoans.helpers";
import edoPerformanceLoansSignal from "./EdoPerformanceLoans.signals";
import { enumCaseToTabCase, titleCaseString } from "../../../../../libs/string";
import BreadCrumbWrapper from "../../../../global/BreadCrumbs/BreadCrumbs";
import { camelCaseToTitleText } from "../../../../../utils/formatTxt";
import { checkEdoProgramPermissions } from '../../../../../libs/edoPermissions';
import loaderSignal from '../../../../../signals/Loader.signal';
import userAccountSignal from '../../../../../signals/UserAccount.signal';
import currencyOptions from 'components/global/Constant/currencyOptions';

const HEADERS = [
  {
    key: 'id',
    title: 'ID',
    sortable: true,
  },
  {
    key: 'lender',
    title: 'Lender',
  },
  {
    key: 'business',
    title: 'Business',
  },
  {
    key: 'status',
    title: 'Status',
  },
  {
    key: 'approved',
    title: 'Approved',
    currency: true,
  },
  {
    key: 'obligated',
    title: 'Obligated',
    currency: true,
  },
  {
    key: 'expended',
    title: 'Expended',
    currency: true,
  },
  {
    key: 'recycled',
    title: 'Recycled',
    currency: true,
  },
  {
    key: 'action',
    title: 'Action',
  },
];

const EdoPerformanceLoans = () => {
  const { applications, fundingProgram } = edoPerformanceLoansSignal.value;
  const history = useHistory();
  const location = useLocation();
  const [canView, setCanView] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Get funding program ID from URL
  const pathSegments = location.pathname.split('/');
  const fundingProgramId = Number(pathSegments[pathSegments.length - 1]);

  // Check permissions for the program
  useEffect(() => {
    const checkPermissions = async () => {
      if (!fundingProgramId || isNaN(fundingProgramId)) {
        setIsChecking(false);
        return;
      }

      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const permissions = await checkEdoProgramPermissions(
          fundingProgramId,
          ['canView']
        );
        setCanView(permissions.canView);
        setIsChecking(false);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setIsChecking(false);
      } finally {
        loaderSignal.reset();
      }
    };

    checkPermissions();
  }, [fundingProgramId, userAccountSignal.value.userData]);

  useEffect(() => {
    if (canView) {
      fetchAndSetApplications();
    }
  }, [history.location.search, canView]);

  if (isChecking) {
    return (
      <BreadCrumbWrapper labels={['Program Performance', '']}>
        <Row>
          <Col xs={9}>
            <Card className="rounded-0">
              <Card.Body>
                <p className="text-center">Loading...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </BreadCrumbWrapper>
    );
  }

  if (!canView) {
    return (
      <BreadCrumbWrapper labels={['Program Performance', '']}>
        <Row>
          <Col xs={9}>
            <Card className="rounded-0">
              <Card.Body>
                <h3 className="text-center">Access Denied</h3>
                <p className="text-center">
                  You do not have permission to view this program&apos;s performance data.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </BreadCrumbWrapper>
    );
  }

  return (
    <BreadCrumbWrapper labels={['Program Performance', fundingProgram?.name || '']}>
      <Row>
        <Col xs={9}>
          <Card className="rounded-0">
            <Card.Body>
              <h3 className="text-center">Loans</h3>
              <hr />
              <div className="d-flex flex-row justify-content-end mb-16">
                <Button
                  className="rounded"
                  onClick={handleDownloadLoansPerformanceProgramData}
                >
                  Download
                </Button>
              </div>
              <SimpleTable
                headers={HEADERS}
                onClick={(index) => {
                  const path = `${history.location.pathname}/application/${applications[index].id}`;
                  history.push(path);
                }}
                rows={applications.map((application) => ({
                  id: application.id,
                  lender:
                    application.fundingProgramMembership?.lender?.name || '?',
                  business: application.business?.name || '?',
                  status: enumCaseToTabCase(application.status),
                  approved: application.approvedAmount || 0,
                  obligated: application.stats.obligated,
                  expended: application.stats.expended,
                  recycled: application.stats.recycled,
                  action: ({ key }) => (
                    <td key={key} className="text-right">
                      <Button
                        size="sm"
                        className="rounded"
                        variant="outline-dark"
                        onClick={() => {
                          const path = `${history.location.pathname}/application/${application.id}`;
                          history.push(path);
                        }}
                      >
                        Application
                      </Button>
                    </td>
                  ),
                }))}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="rounded-0">
            <Card.Body>
              <h4>{fundingProgram?.name}</h4>
              <hr />
              {/*<ProgressBar*/}
              {/*    total={fundingProgram?.stats.allocated}*/}
              {/*    segments={[*/}
              {/*      {*/}
              {/*        amount: fundingProgram?.stats.obligated,*/}
              {/*        color: COLORS.obligated,*/}
              {/*        title: 'Obligated',*/}
              {/*      },*/}
              {/*      {*/}
              {/*        amount: fundingProgram?.stats.expended,*/}
              {/*        color: COLORS.expended,*/}
              {/*        title: 'Expended',*/}
              {/*      },*/}
              {/*    ]}*/}
              {/*/>*/}
              <h5 className="font-600">Program Funds</h5>
              <p>
                {Number(
                  fundingProgram?.ceiling -
                    fundingProgram?.fundsAlreadyAllocated
                ).toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Obligated</h5>
              <p>
                {fundingProgram?.stats.obligated.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Expended</h5>
              <p>
                {fundingProgram?.stats.expended.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Remaining</h5>
              <p>
                {fundingProgram?.stats.remaining.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <hr />
              <h5 className="font-600">Program Config</h5>
              {Object.entries(fundingProgram?.programConfig || {}).map(
                ([key, value]) => (
                  <p key={key}>
                    <strong>{camelCaseToTitleText(key)}:</strong>{' '}
                    {isNaN(Number(value)) ? titleCaseString(value) : value}
                  </p>
                )
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </BreadCrumbWrapper>
  );
};

export default EdoPerformanceLoans;
